"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.downloadScoreReportPdf = exports.openScoreReportPdf = exports.buildScoreReportPdf = void 0;
const jspdf_1 = __importDefault(require("jspdf"));
const FONT_FILE_NAME = 'NotoSansKR-Regular.ttf';
const FONT_FAMILY = 'NotoSansKR';
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const PAGE_MARGIN = 10;
const BOTTOM_MARGIN = 10;
const SUBJECT_LABEL_MAP = {
    korean: '국어',
    english: '영어',
    math: '수학',
    history: '탐구',
    character: '제 2외국어, 한자',
};
const SUBJECT_HEADER_COLOR_MAP = {
    korean: [153, 102, 51],
    english: [175, 23, 191],
    math: [15, 158, 213],
    history: [0, 128, 0],
    character: [23, 131, 170], // fallback blue
};
let fontLoadPromise = null;
const toBase64 = (bytes) => {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};
const loadKoreanFontBase64 = async () => {
    if (!fontLoadPromise) {
        fontLoadPromise = fetch(`/libs/${FONT_FILE_NAME}`)
            .then(res => {
            if (!res.ok)
                throw new Error(`Failed to load font: ${res.status}`);
            return res.arrayBuffer();
        })
            .then(buffer => toBase64(new Uint8Array(buffer)));
    }
    return fontLoadPromise;
};
const trimText = (pdf, text, maxWidth) => {
    if (pdf.getTextWidth(text) <= maxWidth)
        return text;
    let result = text;
    while (result.length > 1 && pdf.getTextWidth(`${result}...`) > maxWidth) {
        result = result.slice(0, -1);
    }
    return `${result}...`;
};
const formatDate = (value) => {
    if (!value)
        return '-';
    return value.length >= 10 ? value.slice(0, 10) : value;
};
const getRawScore = (rawScores, keys) => {
    const found = keys.find(key => rawScores[key] !== undefined);
    return found !== undefined ? rawScores[found] : undefined;
};
// Matches a key that equals the prefix or starts with "<prefix> "
const getRawScoreByPrefix = (rawScores, prefixes) => {
    for (const prefix of prefixes) {
        const key = Object.keys(rawScores).find(k => k === prefix || k.startsWith(`${prefix} `));
        if (key !== undefined && rawScores[key] !== undefined)
            return rawScores[key];
    }
    return undefined;
};
const drawCell = (pdf, x, y, width, height, text, options) => {
    if (options?.fillColor) {
        pdf.setFillColor(...options.fillColor);
        pdf.rect(x, y, width, height, 'F');
    }
    pdf.setDrawColor(...(options?.borderColor || [208, 211, 217]));
    pdf.rect(x, y, width, height);
    pdf.setFont(FONT_FAMILY, options?.fontStyle || 'normal');
    pdf.setTextColor(...(options?.textColor || [16, 16, 16]));
    pdf.setFontSize(options?.fontSize || 9);
    const trimmed = trimText(pdf, text, width - 4);
    const textY = y + height / 2 + 1.5;
    if (options?.align === 'right') {
        pdf.text(trimmed, x + width - 2, textY, { align: 'right' });
        return;
    }
    if (options?.align === 'left') {
        pdf.text(trimmed, x + 2, textY);
        return;
    }
    pdf.text(trimmed, x + width / 2, textY, { align: 'center' });
};
const buildScoreReportPdf = async (payload) => {
    const fontBase64 = await loadKoreanFontBase64();
    const pdf = new jspdf_1.default({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });
    pdf.setProperties({ title: `${payload.report.member_name || 'score-report'}` });
    pdf.addFileToVFS(FONT_FILE_NAME, fontBase64);
    pdf.addFont(FONT_FILE_NAME, FONT_FAMILY, 'normal');
    pdf.addFont(FONT_FILE_NAME, FONT_FAMILY, 'bold');
    pdf.setFont(FONT_FAMILY, 'normal');
    const isYigam = payload.reportType === 'yigam';
    const isEtc = payload.reportType === 'etc';
    const rawScores = payload.report.raw_scores || {};
    const averages = payload.report.averages || {};
    const topAverage = averages[''] ?? '-';
    // etc(기타) 성적표 제목은 등록 시 선택한 상세유형명(reportName)을 사용하고,
    // 상세유형 미선택 시 서버가 '기타'로 내려오므로, 빈값 또는 '기타'면 '기타 성적표'로 폴백한다.
    // (gmk/yigam 제목 로직은 변경하지 않음)
    const etcReportName = typeof payload.reportName === 'string' ? payload.reportName.trim() : '';
    const etcTitle = etcReportName === '' || etcReportName === '기타' ? '기타 성적표' : etcReportName;
    const title = isYigam ? '이감 모의고사 성적표' : isEtc ? etcTitle : '강대모의고사K 성적표';
    const isTamgu = payload.report.question_stats?.some(q => q.subject_item === '탐구');
    const rawSubjectLabel = payload.subjectLabel || '-';
    const mainSubjectName = SUBJECT_LABEL_MAP[rawSubjectLabel] ?? rawSubjectLabel;
    // Build pill label: "수학(미적분)" if a 선택 key exists in raw_scores, otherwise just the subject name
    const selectiveRawKey = Object.keys(rawScores).find(k => k.startsWith('선택 - '));
    const selectiveName = selectiveRawKey ? selectiveRawKey.slice('선택 - '.length) : null;
    const baseSubjectLabel = selectiveName ? `${mainSubjectName}(${selectiveName})` : mainSubjectName;
    const displaySubjectLabel = (isTamgu && payload.report.subject_name) || baseSubjectLabel;
    // ── Header bar ──────────────────────────────────────────────────────────
    // 기타(etc) 성적표는 상세유형/과목과 무관하게 기존 기본 색상([23,131,170])을 유지한다.
    // (과목별 색상 SUBJECT_HEADER_COLOR_MAP은 gmk/yigam/영어 등에만 적용)
    const headerColor = isEtc
        ? [23, 131, 170]
        : SUBJECT_HEADER_COLOR_MAP[rawSubjectLabel] ?? [23, 131, 170];
    pdf.setFillColor(...headerColor);
    pdf.rect(PAGE_MARGIN, PAGE_MARGIN, PAGE_WIDTH - PAGE_MARGIN * 2, 20, 'F');
    // Subject pill (right side) — font size scales down if text is too wide
    const pillX = PAGE_WIDTH - 88;
    const pillW = 52;
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(pillX, 14, pillW, 11, 5, 5, 'F');
    pdf.setFont(FONT_FAMILY, 'normal');
    pdf.setTextColor(16, 16, 16);
    pdf.setFontSize(12);
    const pillLabelW = pdf.getTextWidth(displaySubjectLabel);
    const pillFontSize = pillLabelW > pillW - 4 ? Math.max(7, (12 * (pillW - 4)) / pillLabelW) : 12;
    pdf.setFontSize(pillFontSize);
    pdf.text(displaySubjectLabel, pillX + pillW / 2, 21.5, { align: 'center' });
    // Title: centered in the area left of the pill
    const titleAreaLeft = PAGE_MARGIN + 4;
    const titleAreaRight = pillX - 4;
    const titleCenter = (titleAreaLeft + titleAreaRight) / 2;
    const titleMaxW = titleAreaRight - titleAreaLeft;
    pdf.setFontSize(17);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(FONT_FAMILY, 'bold');
    pdf.text(trimText(pdf, title, titleMaxW), titleCenter, 22, { align: 'center' });
    // ── Meta table ──────────────────────────────────────────────────────────
    const metaX = PAGE_MARGIN;
    const metaY = 36;
    const metaCols = [88, 40, 40, 22];
    const metaLabels = ['테스트명', '강사명', '성명', '실시일'];
    const metaValues = [
        payload.report.test_name || '-',
        payload.report.host_name || '-',
        payload.report.member_name || '-',
        formatDate(payload.testDate),
    ];
    let currentX = metaX;
    for (let i = 0; i < metaCols.length; i += 1) {
        drawCell(pdf, currentX, metaY, metaCols[i], 8, metaLabels[i], {
            fillColor: [245, 246, 248],
            fontStyle: 'bold',
        });
        drawCell(pdf, currentX, metaY + 8, metaCols[i], 12, metaValues[i], {
            fontSize: 8.5,
        });
        currentX += metaCols[i];
    }
    const getSubjectType = (key) => {
        if (key === 'korean' || key === 'math')
            return 'korean_math';
        if (key === 'english')
            return 'english';
        return 'etc';
    };
    const subjectType = getSubjectType(rawSubjectLabel);
    const isEnglish = !isYigam && !isEtc && subjectType === 'english';
    // ── Layout constants ────────────────────────────────────────────────────
    const summaryX = PAGE_MARGIN;
    const summaryY = 72;
    const summaryRowH = 8;
    // etc 와 영어(등급컷 없음)는 우측 등급컷 영역까지 써서 풀폭으로 표시.
    const summaryW = payload.reportType === 'etc' || isEnglish
        ? PAGE_WIDTH - PAGE_MARGIN * 2
        : 142;
    const scoreColW = summaryW / 4;
    const cutoffGap = 11;
    const cutoffX = summaryX + summaryW + cutoffGap; // 163mm
    const cutoffW = PAGE_WIDTH - cutoffX - PAGE_MARGIN; // 37mm
    const headerFill = [245, 246, 248];
    // ── Section titles ──────────────────────────────────────────────────────
    pdf.setFont(FONT_FAMILY, 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(16, 16, 16);
    pdf.text(isYigam ? '개인 성적' : '득점 및 평균', PAGE_MARGIN, 67);
    if (payload.reportType !== 'etc' && !isEnglish) {
        pdf.text('예상 등급컷', cutoffX, 67);
    }
    // ── YIGAM layout: 개인 성적 + 영역별 점수 ───────────────────────────────
    let summaryTableBottom;
    let cutoffTableBottom;
    if (isYigam) {
        // Compute tier from cutoffs when API returns 0
        const computeYigamTier = (score) => {
            const sorted = [...payload.tierCutoffs].sort((a, b) => a.tier - b.tier);
            for (const item of sorted) {
                if (score >= item.score)
                    return item.tier;
            }
            return sorted.length > 0 ? sorted[sorted.length - 1].tier + 1 : '-';
        };
        const rawTier = payload.report.tier;
        const displayTier = (rawTier === 0 || rawTier === null || rawTier === undefined)
            ? computeYigamTier(Number(rawScores[''] ?? 0))
            : rawTier;
        // Pre-compute heights so cutoff table spans the full combined height
        const personalRowH = 6; // header and value rows are both 6mm
        const personalTotalH = personalRowH * 2; // 12mm
        const domainRowH = 6;
        const domainTitleY = summaryY + personalTotalH + 8; // "영역별 점수" title baseline (gap from 개인 성적)
        const domainTableY = domainTitleY + 4; // domain table top
        const domainTableH = 5 * domainRowH; // header + 4 data rows = 30mm
        const yigamTotalH = (domainTableY - summaryY) + domainTableH; // 49mm
        const yigamCutoffRowH = yigamTotalH / 5; // 5 rows (header+4) spanning full height
        const col1W = summaryW / 2;
        const col2W = summaryW / 2;
        // 개인 성적 table
        drawCell(pdf, summaryX, summaryY, col1W, personalRowH, '원점수', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        drawCell(pdf, summaryX + col1W, summaryY, col2W, personalRowH, '등급', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        drawCell(pdf, summaryX, summaryY + personalRowH, col1W, personalRowH, String(rawScores[''] ?? '-'));
        drawCell(pdf, summaryX + col1W, summaryY + personalRowH, col2W, personalRowH, String(displayTier));
        // 예상 등급컷: 4 tiers, spans full combined height (yigamTotalH)
        drawCell(pdf, cutoffX, summaryY, cutoffW / 2, yigamCutoffRowH, '등급', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        drawCell(pdf, cutoffX + cutoffW / 2, summaryY, cutoffW / 2, yigamCutoffRowH, '점수', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        const yigamCutoffRows = payload.tierCutoffs.slice(0, 4);
        for (let i = 0; i < 4; i += 1) {
            const item = yigamCutoffRows[i];
            drawCell(pdf, cutoffX, summaryY + yigamCutoffRowH * (i + 1), cutoffW / 2, yigamCutoffRowH, item ? `${item.tier}등급` : '-', { fontSize: 8.5 });
            drawCell(pdf, cutoffX + cutoffW / 2, summaryY + yigamCutoffRowH * (i + 1), cutoffW / 2, yigamCutoffRowH, item ? String(item.score) : '-', { fontSize: 8.5 });
        }
        cutoffTableBottom = summaryY + yigamTotalH;
        // 영역별 점수 section title
        pdf.setFont(FONT_FAMILY, 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(16, 16, 16);
        pdf.text('영역별 점수', PAGE_MARGIN, domainTitleY);
        const domainCol1W = 60; // 영역
        const domainCol2W = 36; // 점수
        const domainCol3W = summaryW - domainCol1W - domainCol2W; // 대치동 평균 (46mm)
        // 영역별 점수 header row
        drawCell(pdf, summaryX, domainTableY, domainCol1W, domainRowH, '영역', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        drawCell(pdf, summaryX + domainCol1W, domainTableY, domainCol2W, domainRowH, '점수', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        drawCell(pdf, summaryX + domainCol1W + domainCol2W, domainTableY, domainCol3W, domainRowH, '대치동 평균', {
            fillColor: headerFill, fontStyle: 'bold',
        });
        // Compute area scores from question_stats.
        // p79: question_stats 는 선택과목 조합 때문에 공통(독서/문학) 문항이 중복 포함될 수 있어,
        // 문항별 분석 표와 동일한 '문항번호 기준 중복 제거(학생 응답 O/X 우선)'를 적용하지 않으면
        // 영역별 배점 합(괄호 값)이 부풀려진다(총점 168 → 100이어야 함). 점수 열은 correction==='O'
        // 필터라 이미 정상이나, 일관성을 위해 동일한 unique 배열로 배점/점수를 함께 계산한다.
        const areaDedupMap = new Map();
        for (const q of payload.report.question_stats || []) {
            const existing = areaDedupMap.get(q.number);
            if (!existing) {
                areaDedupMap.set(q.number, q);
            }
            else if (existing.correction === '-' && (q.correction === 'O' || q.correction === 'X')) {
                areaDedupMap.set(q.number, q);
            }
        }
        const uniqueQs = Array.from(areaDedupMap.values());
        const computeAreaScore = (from, to) => uniqueQs
            .filter(q => q.number >= from && q.number <= to && q.correction === 'O')
            .reduce((s, q) => s + q.point, 0);
        const computeAreaMax = (from, to) => uniqueQs
            .filter(q => q.number >= from && q.number <= to)
            .reduce((s, q) => s + q.point, 0);
        const dokseMax = computeAreaMax(1, 17);
        const munhakMax = computeAreaMax(18, 34);
        const selectiveMax = computeAreaMax(35, 45);
        const totalMax = dokseMax + munhakMax + selectiveMax;
        const dokseScore = computeAreaScore(1, 17);
        const munhakScore = computeAreaScore(18, 34);
        const selectiveScore = rawScores[selectiveRawKey || ''] ?? computeAreaScore(35, 45);
        const totalScore = rawScores[''] ?? '-';
        const selectiveLabel = selectiveName ?? '선택';
        const selectiveAvgKey = Object.keys(averages).find(k => k.startsWith('선택'));
        const selectiveAvg = selectiveAvgKey !== undefined ? averages[selectiveAvgKey] : '-';
        const domainRows = [
            [`독서(${dokseMax})`, String(dokseScore), String(averages['독서'] ?? '-')],
            [`문학(${munhakMax})`, String(munhakScore), String(averages['문학'] ?? '-')],
            [`${selectiveLabel}(${selectiveMax})`, String(selectiveScore), String(selectiveAvg)],
            [`총점(${totalMax})`, String(totalScore), String(topAverage)],
        ];
        domainRows.forEach(([area, score, avg], rowIdx) => {
            const rowY = domainTableY + domainRowH + domainRowH * rowIdx;
            drawCell(pdf, summaryX, rowY, domainCol1W, domainRowH, area);
            drawCell(pdf, summaryX + domainCol1W, rowY, domainCol2W, domainRowH, score);
            drawCell(pdf, summaryX + domainCol1W + domainCol2W, rowY, domainCol3W, domainRowH, avg);
        });
        summaryTableBottom = summaryY + yigamTotalH;
    }
    else if (isEnglish) {
        // ── English(절대평가): TABLE 1 원점수/등급/최고점 + TABLE 2 등급비율/전체평균 ──
        // 등급컷 표 없음. 데이터: max_score, tier_ratios, averages['']
        const enCol = summaryW / 3;
        const rawTotal = String(getRawScore(rawScores, ['', '계', '합계', 'total', '원점수']) ?? '-');
        const maxScore = String(payload.report.max_score ?? '-');
        const tierVal = String(payload.report.tier ?? '-');
        // TABLE 1: 원점수 | 등급 | 최고점
        ['원점수', '등급', '최고점'].forEach((label, i) => {
            drawCell(pdf, summaryX + enCol * i, summaryY, enCol, summaryRowH, label, { fillColor: headerFill, fontStyle: 'bold' });
        });
        [rawTotal, tierVal, maxScore].forEach((val, i) => {
            drawCell(pdf, summaryX + enCol * i, summaryY + summaryRowH, enCol, summaryRowH, val);
        });
        // TABLE 2: 등급 비율(전체) [1·2·3등급] + 전체 평균
        const t2y = summaryY + summaryRowH * 2;
        const q = summaryW / 4;
        const tierRatios = payload.report.tier_ratios || [];
        const ratioFor = (t) => {
            const found = tierRatios.find(r => r.tier === t);
            return found ? `${(found.ratio * 100).toFixed(1)}%` : '-';
        };
        drawCell(pdf, summaryX, t2y, q * 3, summaryRowH, '등급 비율(전체)', { fillColor: headerFill, fontStyle: 'bold' });
        drawCell(pdf, summaryX + q * 3, t2y, q, summaryRowH * 2, '전체 평균', { fillColor: headerFill, fontStyle: 'bold' });
        ['1등급', '2등급', '3등급'].forEach((label, i) => {
            drawCell(pdf, summaryX + q * i, t2y + summaryRowH, q, summaryRowH, label, { fillColor: headerFill, fontStyle: 'bold' });
        });
        [ratioFor(1), ratioFor(2), ratioFor(3)].forEach((val, i) => {
            drawCell(pdf, summaryX + q * i, t2y + summaryRowH * 2, q, summaryRowH, val);
        });
        drawCell(pdf, summaryX + q * 3, t2y + summaryRowH * 2, q, summaryRowH, String(topAverage));
        summaryTableBottom = t2y + summaryRowH * 3;
        cutoffTableBottom = summaryY; // 영어는 등급컷 없음
    }
    else {
        // ── GMK layout: TABLE 1 원점수 + 표준점수/전체평균 ────────────────────
        // 표준점수 컬럼(GMK): 성적표 데이터에 표준점수 값이 입력된 경우에만 노출, 미입력 시 미노출.
        // (etc 리포트의 '전체 평균'은 항상 표시)
        const SHOW_STANDARD_SCORE = payload.report.standard_score != null && String(payload.report.standard_score).trim() !== '';
        const table1H = summaryRowH * 3;
        const isEtcReportType = payload.reportType === 'etc';
        // 전체 평균(etc)은 항상 표시, 표준점수(GMK)는 플래그로 제어
        const showCol4 = isEtcReportType || SHOW_STANDARD_SCORE;
        const col4Label = isEtcReportType ? '전체 평균' : '표준점수';
        const col4Value = isEtcReportType ? String(topAverage) : String(payload.report.standard_score ?? '-');
        if (subjectType === 'korean_math' || subjectType === 'english') {
            const col1Label = subjectType === 'english' ? '듣기' : '공통';
            const col2Label = subjectType === 'english' ? '읽기' : '선택';
            const score1 = String(subjectType === 'english'
                ? getRawScore(rawScores, ['듣기', 'listening']) ?? '-'
                : getRawScore(rawScores, ['공통', 'common']) ?? '-');
            const score2 = String(subjectType === 'english'
                ? getRawScore(rawScores, ['읽기', 'reading']) ?? '-'
                : getRawScoreByPrefix(rawScores, ['선택', 'elective']) ?? '-');
            const totalScore = String(getRawScore(rawScores, ['', '계', '합계', 'total']) ?? '-');
            // showCol4: 원점수(3칸) + col4(1칸) / 비활성: 원점수 전체 폭(3등분)
            const headerW = showCol4 ? scoreColW * 3 : summaryW;
            const subColW = showCol4 ? scoreColW : summaryW / 3;
            drawCell(pdf, summaryX, summaryY, headerW, summaryRowH, '원점수', { fillColor: headerFill, fontStyle: 'bold' });
            if (showCol4) {
                drawCell(pdf, summaryX + scoreColW * 3, summaryY, scoreColW, summaryRowH * 2, col4Label, { fillColor: headerFill, fontStyle: 'bold' });
            }
            [col1Label, col2Label, '계'].forEach((label, i) => {
                drawCell(pdf, summaryX + subColW * i, summaryY + summaryRowH, subColW, summaryRowH, label, { fillColor: headerFill, fontStyle: 'bold' });
            });
            [score1, score2, totalScore].forEach((val, i) => {
                drawCell(pdf, summaryX + subColW * i, summaryY + summaryRowH * 2, subColW, summaryRowH, val);
            });
            if (showCol4) {
                drawCell(pdf, summaryX + scoreColW * 3, summaryY + summaryRowH * 2, scoreColW, summaryRowH, col4Value);
            }
        }
        else {
            const etcRowH = table1H / 2;
            const singleScore = String(getRawScore(rawScores, ['원점수', '총점', 'total', '', '계', '합계']) ?? '-');
            // showCol4: 원점수(반) + col4(반) / 비활성: 원점수 전체 폭
            const labelW = showCol4 ? summaryW / 2 : summaryW;
            drawCell(pdf, summaryX, summaryY, labelW, etcRowH, '원점수', { fillColor: headerFill, fontStyle: 'bold' });
            if (showCol4) {
                drawCell(pdf, summaryX + summaryW / 2, summaryY, summaryW / 2, etcRowH, col4Label, { fillColor: headerFill, fontStyle: 'bold' });
            }
            drawCell(pdf, summaryX, summaryY + etcRowH, labelW, etcRowH, singleScore);
            if (showCol4) {
                drawCell(pdf, summaryX + summaryW / 2, summaryY + etcRowH, summaryW / 2, etcRowH, col4Value);
            }
        }
        // ── TABLE 2: 수능예상 + 전체 평균 ────────────────────────────────────
        const table2Y = summaryY + summaryRowH * 3;
        if (!isEtcReportType) {
            const t2W1 = scoreColW * 3;
            const t2W2 = scoreColW;
            const t2Sub = t2W1 / 2;
            drawCell(pdf, summaryX, table2Y, t2W1, summaryRowH, '수능예상', { fillColor: headerFill, fontStyle: 'bold' });
            drawCell(pdf, summaryX + t2W1, table2Y, t2W2, summaryRowH * 2, '전체 평균', { fillColor: headerFill, fontStyle: 'bold' });
            drawCell(pdf, summaryX, table2Y + summaryRowH, t2Sub, summaryRowH, '백분위', { fillColor: headerFill, fontStyle: 'bold' });
            drawCell(pdf, summaryX + t2Sub, table2Y + summaryRowH, t2Sub, summaryRowH, '등급', { fillColor: headerFill, fontStyle: 'bold' });
            drawCell(pdf, summaryX, table2Y + summaryRowH * 2, t2Sub, summaryRowH, String(payload.report.percentile ?? '-'));
            drawCell(pdf, summaryX + t2Sub, table2Y + summaryRowH * 2, t2Sub, summaryRowH, String(payload.report.tier ?? '-'));
            drawCell(pdf, summaryX + t2W1, table2Y + summaryRowH * 2, t2W2, summaryRowH, String(topAverage));
        }
        const isEtcReportType2 = payload.reportType === 'etc';
        const table1H2 = summaryRowH * 3;
        summaryTableBottom = isEtcReportType2 ? summaryY + table1H2 : table2Y + summaryRowH * 3;
        // ── Cutoff table (right column) ──────────────────────────────────────
        const totalLeftH = isEtcReportType2 ? table1H2 : table1H2 + summaryRowH * 3;
        const cutoffRowH = totalLeftH / 6;
        cutoffTableBottom = summaryY + totalLeftH;
        if (payload.reportType !== 'etc') {
            drawCell(pdf, cutoffX, summaryY, cutoffW / 2, cutoffRowH, '등급', { fillColor: headerFill, fontStyle: 'bold' });
            drawCell(pdf, cutoffX + cutoffW / 2, summaryY, cutoffW / 2, cutoffRowH, '점수', { fillColor: headerFill, fontStyle: 'bold' });
            const cutoffRows = payload.tierCutoffs.slice(0, 5);
            for (let i = 0; i < 5; i += 1) {
                const item = cutoffRows[i];
                drawCell(pdf, cutoffX, summaryY + cutoffRowH * (i + 1), cutoffW / 2, cutoffRowH, item ? `${item.tier}등급` : '-', { fontSize: 8.5 });
                drawCell(pdf, cutoffX + cutoffW / 2, summaryY + cutoffRowH * (i + 1), cutoffW / 2, cutoffRowH, item ? String(item.score) : '-', { fontSize: 8.5 });
            }
        }
    }
    // ── 회차별 성적 section(s) ───────────────────────────────────────────────
    const chartSectionStartY = Math.max(summaryTableBottom, cutoffTableBottom) + 6;
    const gradeRows = payload.gradeReport;
    // 회차 라벨 — 재응시는 markRetakeRounds 가 붙인 "N회차(재)", 그 외 "N회차"(회차 없으면 표시 순번)
    const roundLabel = (item, fallback) => item.round_label ?? `${item.round ?? fallback}회차`;
    // Build chunks of 8
    const CHUNK_SIZE = 8;
    const chunks = [];
    for (let i = 0; i < gradeRows.length; i += CHUNK_SIZE) {
        chunks.push(gradeRows.slice(i, i + CHUNK_SIZE));
    }
    if (chunks.length === 0)
        chunks.push([]);
    // Layout constants for each chunk
    const graphXLabelPad = 10;
    const rowLabelH = 8;
    // Yigam: 2 rows (내 점수 + 대치동); Etc: 1 row (원점수 only); English: 2 rows (등급 + 원점수); GMK: 3 rows (백분위 + 등급 + 원점수)
    const tableRows = isYigam ? 2 : isEtc ? 1 : isEnglish ? 2 : 3;
    const tableH = tableRows * rowLabelH;
    // Shrink graph height when multiple chunks need to fit on one page.
    // First chunk includes title (11mm); subsequent chunks skip it.
    const firstChunkFixed = 11 + 4 + graphXLabelPad + tableH;
    const otherChunkFixed = 4 + graphXLabelPad + tableH;
    const availableForChunks = PAGE_HEIGHT - BOTTOM_MARGIN - chartSectionStartY;
    const totalGapBetweenChunks = (chunks.length - 1) * 6;
    const computedGraphH = chunks.length > 1
        ? (availableForChunks - totalGapBetweenChunks - firstChunkFixed - (chunks.length - 1) * otherChunkFixed) / chunks.length
        : 92;
    const graphHeight = computedGraphH >= 15 ? computedGraphH : 92;
    const isScaled = graphHeight !== 92;
    // yigam: no 수능예상 merged label so label column is a single cell
    const labelW = isYigam ? 18 : 22; // total label col width
    const valueAreaW = PAGE_WIDTH - PAGE_MARGIN * 2 - labelW;
    const valueColW = valueAreaW / 8;
    const firstSectionH = firstChunkFixed + graphHeight;
    const otherSectionH = otherChunkFixed + graphHeight;
    const drawChunk = (chunk, startY, chunkIndex) => {
        const isFirstChunk = chunkIndex === 0;
        // Section title — first chunk only
        if (isFirstChunk) {
            pdf.setFont(FONT_FAMILY, 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(16, 16, 16);
            pdf.text('회차별 성적', PAGE_MARGIN, startY + 8);
        }
        const graphTop = startY + (isFirstChunk ? 11 : 0) + 4;
        const graphLeft = PAGE_MARGIN + labelW;
        const graphRight = PAGE_WIDTH - PAGE_MARGIN;
        if (isYigam) {
            // ── Yigam: legend top-right (first chunk only) ──────────────────
            if (isFirstChunk) {
                const legendX = PAGE_WIDTH - PAGE_MARGIN;
                const legendY = startY + 8;
                const sq = 2.5;
                const sqGap = 1.5;
                const sqTop = legendY - sq / 2 - 1;
                pdf.setFontSize(8);
                pdf.setFont(FONT_FAMILY, 'normal');
                pdf.setTextColor(90, 90, 90);
                // 대치동 (rightmost)
                const daechiLabel = '대치동';
                const daechiLabelW = pdf.getTextWidth(daechiLabel);
                pdf.text(daechiLabel, legendX, legendY, { align: 'right' });
                const daechiSqX = legendX - daechiLabelW - sqGap - sq;
                pdf.setFillColor(200, 200, 200);
                pdf.rect(daechiSqX, sqTop, sq, sq, 'F');
                pdf.setDrawColor(170, 170, 170);
                pdf.rect(daechiSqX, sqTop, sq, sq);
                // 내 점수 (left of 대치동)
                const myLabel = '내 점수';
                const myLabelW = pdf.getTextWidth(myLabel);
                const myTextX = daechiSqX - 5;
                pdf.text(myLabel, myTextX, legendY, { align: 'right' });
                const mySqX = myTextX - myLabelW - sqGap - sq;
                pdf.setFillColor(80, 80, 80);
                pdf.rect(mySqX, sqTop, sq, sq, 'F');
                pdf.setDrawColor(50, 50, 50);
                pdf.rect(mySqX, sqTop, sq, sq);
            }
            // ── Yigam: bar chart (raw scores 0–100) ─────────────────────────
            pdf.setDrawColor(224, 227, 232);
            for (let i = 0; i <= 4; i += 1) {
                const lineY = graphTop + (graphHeight / 4) * i;
                pdf.line(graphLeft, lineY, graphRight, lineY);
                pdf.setFontSize(7);
                pdf.setTextColor(130, 130, 130);
                pdf.text(String(100 - i * 25), graphLeft - 2, lineY + 1.5, { align: 'right' });
            }
            const singleBarW = 4; // narrow bars
            const barGap = 1;
            chunk.forEach((item, index) => {
                const colCenterX = graphLeft + valueColW * index + valueColW / 2;
                const myBarX = colCenterX - singleBarW - barGap / 2;
                const daechiBarX = colCenterX + barGap / 2;
                const myScore = Math.max(0, Math.min(100, Number(item.raw_score || 0)));
                const daechiScore = Math.max(0, Math.min(100, Number(item.daechi ?? 0)));
                // bars anchored to the 0-line (graphTop + graphHeight)
                const myBarH = graphHeight * (myScore / 100);
                const daechiBarH = graphHeight * (daechiScore / 100);
                if (myBarH > 0) {
                    pdf.setFillColor(80, 80, 80);
                    pdf.rect(myBarX, graphTop + graphHeight - myBarH, singleBarW, myBarH, 'F');
                }
                if (daechiBarH > 0) {
                    pdf.setFillColor(200, 200, 200);
                    pdf.rect(daechiBarX, graphTop + graphHeight - daechiBarH, singleBarW, daechiBarH, 'F');
                }
                pdf.setFontSize(7);
                pdf.setTextColor(90, 90, 90);
                pdf.text(roundLabel(item, chunkIndex * CHUNK_SIZE + index + 1), colCenterX, graphTop + graphHeight + 5, { align: 'center' });
            });
            // ── Yigam: table — 내 점수 + 대치동 ─────────────────────────────
            const tableTop = graphTop + graphHeight + graphXLabelPad;
            const drawYigamRow = (rowIndex, label, getValue) => {
                drawCell(pdf, PAGE_MARGIN, tableTop + rowIndex * rowLabelH, labelW, rowLabelH, label, {
                    fillColor: [245, 246, 248], fontStyle: 'bold', fontSize: 8,
                });
                for (let colIndex = 0; colIndex < 8; colIndex += 1) {
                    const item = chunk[colIndex];
                    const val = item !== undefined ? getValue(item) : undefined;
                    drawCell(pdf, PAGE_MARGIN + labelW + valueColW * colIndex, tableTop + rowIndex * rowLabelH, valueColW, rowLabelH, val === undefined || val === null ? '-' : String(val), { fontSize: 8 });
                }
            };
            drawYigamRow(0, '내 점수', item => item.raw_score);
            drawYigamRow(1, '대치동', item => item.daechi ?? '-');
        }
        else if (isEtc) {
            // ── Etc: bar chart (raw score 0–100) ─────────────────────────────
            const barWidth = Math.min(8, valueColW - 4);
            if (isFirstChunk) {
                pdf.setFont(FONT_FAMILY, 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(90, 90, 90);
                const etcLegendX = PAGE_WIDTH - PAGE_MARGIN;
                const etcLegendY = startY + 8;
                const etcSq = 2.5;
                const etcSqGap = 1.5;
                const etcSqTop = etcLegendY - etcSq / 2 - 1;
                const etcLabel = '원점수';
                const etcLabelW = pdf.getTextWidth(etcLabel);
                pdf.text(etcLabel, etcLegendX, etcLegendY, { align: 'right' });
                const etcSqX = etcLegendX - etcLabelW - etcSqGap - etcSq;
                pdf.setFillColor(205, 208, 214);
                pdf.rect(etcSqX, etcSqTop, etcSq, etcSq, 'F');
                pdf.setDrawColor(175, 178, 184);
                pdf.rect(etcSqX, etcSqTop, etcSq, etcSq);
            }
            pdf.setDrawColor(224, 227, 232);
            for (let i = 0; i <= 4; i += 1) {
                const lineY = graphTop + (graphHeight / 4) * i;
                pdf.line(graphLeft, lineY, graphRight, lineY);
                pdf.setFontSize(7);
                pdf.setTextColor(130, 130, 130);
                pdf.text(String(100 - i * 25), graphLeft - 2, lineY + 1.5, { align: 'right' });
            }
            chunk.forEach((item, index) => {
                const colCenterX = graphLeft + valueColW * index + valueColW / 2;
                const barX = colCenterX - barWidth / 2;
                const rawScore = Math.max(0, Math.min(100, Number(item.raw_score || 0)));
                const barHeight = graphHeight * (rawScore / 100);
                const barY = graphTop + graphHeight - barHeight;
                pdf.setFillColor(205, 208, 214);
                pdf.rect(barX, barY, barWidth, barHeight, 'F');
                pdf.setFontSize(7);
                pdf.setTextColor(90, 90, 90);
                pdf.text(roundLabel(item, chunkIndex * CHUNK_SIZE + index + 1), colCenterX, graphTop + graphHeight + 5, { align: 'center' });
            });
            // ── Etc: table — 원점수 only ──────────────────────────────────────
            const tableTop = graphTop + graphHeight + graphXLabelPad;
            drawCell(pdf, PAGE_MARGIN, tableTop, labelW, rowLabelH, '원점수', {
                fillColor: [245, 246, 248], fontStyle: 'bold', fontSize: 7.5,
            });
            for (let colIndex = 0; colIndex < 8; colIndex += 1) {
                const item = chunk[colIndex];
                drawCell(pdf, PAGE_MARGIN + labelW + valueColW * colIndex, tableTop, valueColW, rowLabelH, item !== undefined ? String(item.raw_score ?? '-') : '-', { fontSize: 8 });
            }
        }
        else if (isEnglish) {
            // ── English: 원점수 막대그래프 + (등급/원점수) 표 ─────────────────
            const barWidth = Math.min(8, valueColW - 4);
            if (isFirstChunk) {
                pdf.setFont(FONT_FAMILY, 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(90, 90, 90);
                const enLegendX = PAGE_WIDTH - PAGE_MARGIN;
                const enLegendY = startY + 8;
                const enSq = 2.5;
                const enSqGap = 1.5;
                const enSqTop = enLegendY - enSq / 2 - 1;
                const enLabel = '등급';
                const enLabelW = pdf.getTextWidth(enLabel);
                pdf.text(enLabel, enLegendX, enLegendY, { align: 'right' });
                const enSqX = enLegendX - enLabelW - enSqGap - enSq;
                pdf.setFillColor(205, 208, 214);
                pdf.rect(enSqX, enSqTop, enSq, enSq, 'F');
                pdf.setDrawColor(175, 178, 184);
                pdf.rect(enSqX, enSqTop, enSq, enSq);
            }
            pdf.setDrawColor(224, 227, 232);
            // 등급 축: 1등급(상단)~9등급(하단). 그리드 1·3·5·7·9등급.
            for (let i = 0; i <= 4; i += 1) {
                const lineY = graphTop + (graphHeight / 4) * i;
                pdf.line(graphLeft, lineY, graphRight, lineY);
                pdf.setFontSize(7);
                pdf.setTextColor(130, 130, 130);
                pdf.text(`${1 + i * 2}등급`, graphLeft - 2, lineY + 1.5, { align: 'right' });
            }
            // 막대: 등급 기준(낮은 등급=좋음=높은 막대). 1등급→풀높이, 9등급→0.
            chunk.forEach((item, index) => {
                const colCenterX = graphLeft + valueColW * index + valueColW / 2;
                const barX = colCenterX - barWidth / 2;
                const tierNum = Number(item.tier);
                if (Number.isFinite(tierNum) && tierNum >= 1) {
                    const tier = Math.max(1, Math.min(9, tierNum));
                    const barHeight = (graphHeight * (9 - tier)) / 8;
                    const barY = graphTop + graphHeight - barHeight;
                    pdf.setFillColor(205, 208, 214);
                    pdf.rect(barX, barY, barWidth, barHeight, 'F');
                }
                pdf.setFontSize(7);
                pdf.setTextColor(90, 90, 90);
                pdf.text(roundLabel(item, chunkIndex * CHUNK_SIZE + index + 1), colCenterX, graphTop + graphHeight + 5, { align: 'center' });
            });
            // ── English: table — 등급 + 원점수 ───────────────────────────────
            const tableTop = graphTop + graphHeight + graphXLabelPad;
            const enRows = [
                ['등급', item => item.tier],
                ['원점수', item => item.raw_score],
            ];
            enRows.forEach(([label, getVal], rowIndex) => {
                drawCell(pdf, PAGE_MARGIN, tableTop + rowIndex * rowLabelH, labelW, rowLabelH, label, {
                    fillColor: [245, 246, 248], fontStyle: 'bold', fontSize: 8,
                });
                for (let colIndex = 0; colIndex < 8; colIndex += 1) {
                    const item = chunk[colIndex];
                    const val = item !== undefined ? getVal(item) : undefined;
                    drawCell(pdf, PAGE_MARGIN + labelW + valueColW * colIndex, tableTop + rowIndex * rowLabelH, valueColW, rowLabelH, val === undefined || val === null ? '-' : String(val), { fontSize: 8 });
                }
            });
        }
        else {
            // ── GMK: bar chart (percentile) ──────────────────────────────────
            const barWidth = Math.min(8, valueColW - 4);
            if (isFirstChunk) {
                pdf.setFont(FONT_FAMILY, 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(90, 90, 90);
                const pctLegendX = PAGE_WIDTH - PAGE_MARGIN;
                const pctLegendY = startY + 8;
                const pctSq = 2.5;
                const pctSqGap = 1.5;
                const pctSqTop = pctLegendY - pctSq / 2 - 1;
                const pctLabel = '예상 백분위';
                const pctLabelW = pdf.getTextWidth(pctLabel);
                pdf.text(pctLabel, pctLegendX, pctLegendY, { align: 'right' });
                const pctSqX = pctLegendX - pctLabelW - pctSqGap - pctSq;
                pdf.setFillColor(205, 208, 214);
                pdf.rect(pctSqX, pctSqTop, pctSq, pctSq, 'F');
                pdf.setDrawColor(175, 178, 184);
                pdf.rect(pctSqX, pctSqTop, pctSq, pctSq);
            }
            pdf.setDrawColor(224, 227, 232);
            for (let i = 0; i <= 4; i += 1) {
                const lineY = graphTop + (graphHeight / 4) * i;
                pdf.line(graphLeft, lineY, graphRight, lineY);
                pdf.setFontSize(7);
                pdf.setTextColor(130, 130, 130);
                pdf.text(String(100 - i * 25), graphLeft - 2, lineY + 1.5, { align: 'right' });
            }
            chunk.forEach((item, index) => {
                const colCenterX = graphLeft + valueColW * index + valueColW / 2;
                const barX = colCenterX - barWidth / 2;
                const percentile = Math.max(0, Math.min(100, Number(item.percentile || 0)));
                const barHeight = (graphHeight - 10) * (percentile / 100);
                const barY = graphTop + graphHeight - barHeight;
                pdf.setFillColor(205, 208, 214);
                pdf.rect(barX, barY, barWidth, barHeight, 'F');
                pdf.setFontSize(7);
                pdf.setTextColor(90, 90, 90);
                pdf.text(roundLabel(item, chunkIndex * CHUNK_SIZE + index + 1), colCenterX, graphTop + graphHeight + 5, { align: 'center' });
            });
            // ── GMK: table — 수능예상(백분위/등급) + 원점수 ──────────────────
            const tableTop = graphTop + graphHeight + graphXLabelPad;
            const labelW1 = 10;
            const labelW2 = 12;
            const drawDataRow = (rowIndex, value, labelXArg, labelCellW, label) => {
                drawCell(pdf, labelXArg, tableTop + rowIndex * rowLabelH, labelCellW, rowLabelH, label, {
                    fillColor: [245, 246, 248], fontStyle: 'bold', fontSize: 7.5,
                });
                for (let colIndex = 0; colIndex < 8; colIndex += 1) {
                    const item = chunk[colIndex];
                    const val = item !== undefined ? value(item) : undefined;
                    drawCell(pdf, PAGE_MARGIN + labelW + valueColW * colIndex, tableTop + rowIndex * rowLabelH, valueColW, rowLabelH, val === undefined || val === null ? '-' : String(val), { fontSize: 8 });
                }
            };
            pdf.setFillColor(245, 246, 248);
            pdf.rect(PAGE_MARGIN, tableTop, labelW1, rowLabelH * 2, 'F');
            pdf.setDrawColor(208, 211, 217);
            pdf.rect(PAGE_MARGIN, tableTop, labelW1, rowLabelH * 2);
            pdf.setFont(FONT_FAMILY, 'bold');
            pdf.setFontSize(7);
            pdf.setTextColor(16, 16, 16);
            pdf.text('수능', PAGE_MARGIN + labelW1 / 2, tableTop + rowLabelH * 2 / 2 - 1.5, { align: 'center' });
            pdf.text('예상', PAGE_MARGIN + labelW1 / 2, tableTop + rowLabelH * 2 / 2 + 4, { align: 'center' });
            drawDataRow(0, item => item.percentile, PAGE_MARGIN + labelW1, labelW2, '백분위');
            drawDataRow(1, item => item.tier, PAGE_MARGIN + labelW1, labelW2, '등급');
            drawDataRow(2, item => item.raw_score, PAGE_MARGIN, labelW, '원점수');
        }
    };
    const shouldShowGradeReport = payload.reportType !== 'etc' || payload.showGradeReport !== false;
    if (shouldShowGradeReport) {
        let currentY = chartSectionStartY;
        chunks.forEach((chunk, chunkIndex) => {
            const sectionH = chunkIndex === 0 ? firstSectionH : otherSectionH;
            if (!isScaled && chunkIndex > 0) {
                if (currentY + sectionH > PAGE_HEIGHT - BOTTOM_MARGIN) {
                    pdf.addPage();
                    currentY = PAGE_MARGIN;
                }
            }
            drawChunk(chunk, currentY, chunkIndex);
            currentY += sectionH + 6;
        });
    }
    // ── 문항별 분석 page ─────────────────────────────────────────────────────
    const questionStats = payload.report.question_stats;
    if (questionStats && questionStats.length > 0) {
        const deduplicateQuestions = (stats) => {
            const map = new Map();
            for (const q of stats) {
                const existing = map.get(q.number);
                if (!existing) {
                    map.set(q.number, q);
                }
                else if (existing.correction === '-' && (q.correction === 'O' || q.correction === 'X')) {
                    map.set(q.number, q);
                }
            }
            return Array.from(map.values()).sort((a, b) => a.number - b.number);
        };
        const uniqueQuestions = deduplicateQuestions(questionStats);
        const RED_TEXT = [220, 50, 50];
        const DIVIDER_FILL = [245, 246, 248];
        const TOTAL_W = PAGE_WIDTH - PAGE_MARGIN * 2;
        let qaCurrentY = 0;
        if (isYigam) {
            // ── Yigam 문항별 분석 ─────────────────────────────────────────────
            // 문항번호 column = section_sidebar + qnum, split 50/50 inside.
            // section(12) + 7 × colW = 190  →  colW ≈ 25.43mm
            // halfQN = (12 + 25.43) / 2 ≈ 18.71mm  → section = qnum = 18.71mm
            // Other 6 data cols: colW ≈ 25.43mm each. Total = 2×18.71 + 6×25.43 = 190 ✓
            const YQ_BASE_SECTION = 12;
            const YQ_COL_W = (PAGE_WIDTH - PAGE_MARGIN * 2 - YQ_BASE_SECTION) / 7; // ≈ 25.43mm
            const YQ_HALF_QN = (YQ_BASE_SECTION + YQ_COL_W) / 2; // ≈ 18.71mm (section = qnum)
            const YQ_SECTION_W = YQ_HALF_QN;
            // Dynamic row height: fills exactly one page
            // Available = PAGE_HEIGHT - top_margin - title(12) - bottom_margin
            const yigamQCount = uniqueQuestions.filter(q => q.number >= 1 && q.number <= 45).length;
            const qaPageAvailH = PAGE_HEIGHT - 2 * PAGE_MARGIN - 12; // 265mm
            const YQ_ROW_H = qaPageAvailH / (2 + yigamQCount); // 2 header rows + data rows
            const YQ_HDR_H = YQ_ROW_H;
            // X positions
            const YQ_X = PAGE_MARGIN;
            const YQ_X_QNUM = YQ_X + YQ_SECTION_W; // qnum col = YQ_HALF_QN wide
            const YQ_X_POINT = YQ_X_QNUM + YQ_HALF_QN; // 6 equal data cols start here
            const YQ_X_ANS = YQ_X_POINT + YQ_COL_W;
            const YQ_X_MY = YQ_X_ANS + YQ_COL_W;
            const YQ_X_CHECK = YQ_X_MY + YQ_COL_W;
            const YQ_X_RATE = YQ_X_CHECK + YQ_COL_W;
            const YQ_X_RANK = YQ_X_RATE + YQ_COL_W;
            const drawYigamQaHeader = (headerY) => {
                // "문항번호" spans section + qnum (= 2 × YQ_HALF_QN), single line
                drawCell(pdf, YQ_X, headerY, YQ_HALF_QN * 2, YQ_HDR_H * 2, '문항번호', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, YQ_X_POINT, headerY, YQ_COL_W, YQ_HDR_H * 2, '배점', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, YQ_X_ANS, headerY, YQ_COL_W, YQ_HDR_H * 2, '정답', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, YQ_X_MY, headerY, YQ_COL_W, YQ_HDR_H * 2, '내 답안', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, YQ_X_CHECK, headerY, YQ_COL_W, YQ_HDR_H * 2, '채점', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                // 오답률(%) header spans % + 순위
                drawCell(pdf, YQ_X_RATE, headerY, YQ_COL_W * 2, YQ_HDR_H, '오답률(%)', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, YQ_X_RATE, headerY + YQ_HDR_H, YQ_COL_W, YQ_HDR_H, '%', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, YQ_X_RANK, headerY + YQ_HDR_H, YQ_COL_W, YQ_HDR_H, '순위', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
            };
            const drawYigamQaPageTop = (startY) => {
                qaCurrentY = startY ?? PAGE_MARGIN;
                pdf.setFont(FONT_FAMILY, 'bold');
                pdf.setFontSize(11);
                pdf.setTextColor(16, 16, 16);
                pdf.text('문항별 분석', PAGE_MARGIN, qaCurrentY + 8);
                qaCurrentY += 12;
                drawYigamQaHeader(qaCurrentY);
                qaCurrentY += YQ_HDR_H * 2;
            };
            const ensureSpaceY = (neededH) => {
                if (qaCurrentY + neededH > PAGE_HEIGHT - BOTTOM_MARGIN) {
                    pdf.addPage();
                    drawYigamQaPageTop();
                }
            };
            const drawYigamQaRow = (q, rowY) => {
                const myAnsColor = q.correction === 'X' ? RED_TEXT : [16, 16, 16];
                drawCell(pdf, YQ_X_QNUM, rowY, YQ_HALF_QN, YQ_ROW_H, String(q.number), { fontSize: 7.5 });
                drawCell(pdf, YQ_X_POINT, rowY, YQ_COL_W, YQ_ROW_H, String(q.point), { fontSize: 7.5 });
                drawCell(pdf, YQ_X_ANS, rowY, YQ_COL_W, YQ_ROW_H, q.answer || '-', { fontSize: 7.5 });
                drawCell(pdf, YQ_X_MY, rowY, YQ_COL_W, YQ_ROW_H, q.my_answer || '-', { fontSize: 7.5, textColor: myAnsColor });
                drawCell(pdf, YQ_X_CHECK, rowY, YQ_COL_W, YQ_ROW_H, q.correction || '-', { fontSize: 7.5 });
                drawCell(pdf, YQ_X_RATE, rowY, YQ_COL_W, YQ_ROW_H, String(q.rating ?? '-'), { fontSize: 7.5 });
                drawCell(pdf, YQ_X_RANK, rowY, YQ_COL_W, YQ_ROW_H, q.rank ? String(q.rank) : '', { fontSize: 7.5 });
            };
            const yigamSections = [
                { label: '독서', from: 1, to: 17 },
                { label: '문학', from: 18, to: 34 },
                { label: selectiveName ?? '선택', from: 35, to: 45 },
            ];
            if (shouldShowGradeReport) {
                pdf.addPage();
                drawYigamQaPageTop();
            }
            else {
                drawYigamQaPageTop(chartSectionStartY);
            }
            for (const section of yigamSections) {
                const sectionQs = uniqueQuestions.filter(q => q.number >= section.from && q.number <= section.to);
                if (sectionQs.length === 0)
                    continue;
                ensureSpaceY(YQ_ROW_H);
                const sectionStartY = qaCurrentY;
                const sectionTotalH = sectionQs.length * YQ_ROW_H;
                const fitsOnPage = sectionStartY + sectionTotalH <= PAGE_HEIGHT - BOTTOM_MARGIN;
                if (fitsOnPage) {
                    // Draw section label spanning all rows at once
                    pdf.setFillColor(...DIVIDER_FILL);
                    pdf.rect(YQ_X, sectionStartY, YQ_SECTION_W, sectionTotalH, 'F');
                    pdf.setDrawColor(208, 211, 217);
                    pdf.rect(YQ_X, sectionStartY, YQ_SECTION_W, sectionTotalH);
                    pdf.setFont(FONT_FAMILY, 'bold');
                    pdf.setFontSize(7.5);
                    pdf.setTextColor(16, 16, 16);
                    pdf.text(section.label, YQ_X + YQ_SECTION_W / 2, sectionStartY + sectionTotalH / 2 + 1.5, { align: 'center' });
                    sectionQs.forEach((q, idx) => {
                        drawYigamQaRow(q, sectionStartY + idx * YQ_ROW_H);
                    });
                    qaCurrentY = sectionStartY + sectionTotalH;
                }
                else {
                    // Section spans pages: flush section label segment before each page break
                    let segStartY = sectionStartY;
                    let segCount = 0;
                    const flushLabel = (ly, cnt) => {
                        if (cnt === 0)
                            return;
                        const lh = cnt * YQ_ROW_H;
                        pdf.setFillColor(...DIVIDER_FILL);
                        pdf.rect(YQ_X, ly, YQ_SECTION_W, lh, 'F');
                        pdf.setDrawColor(208, 211, 217);
                        pdf.rect(YQ_X, ly, YQ_SECTION_W, lh);
                        pdf.setFont(FONT_FAMILY, 'bold');
                        pdf.setFontSize(7.5);
                        pdf.setTextColor(16, 16, 16);
                        pdf.text(section.label, YQ_X + YQ_SECTION_W / 2, ly + lh / 2 + 1.5, { align: 'center' });
                    };
                    for (const q of sectionQs) {
                        if (qaCurrentY + YQ_ROW_H > PAGE_HEIGHT - BOTTOM_MARGIN) {
                            flushLabel(segStartY, segCount);
                            pdf.addPage();
                            drawYigamQaPageTop();
                            segStartY = qaCurrentY;
                            segCount = 0;
                        }
                        drawYigamQaRow(q, qaCurrentY);
                        qaCurrentY += YQ_ROW_H;
                        segCount += 1;
                    }
                    flushLabel(segStartY, segCount);
                }
            }
        }
        else {
            // ── GMK 문항별 분석 ───────────────────────────────────────────────
            const commonQs = uniqueQuestions.filter(q => q.subject_item === '공통');
            const selectiveQs = uniqueQuestions.filter(q => q.subject_item !== '공통');
            const QA_ROW_H = 7;
            const QA_HDR_H = 7;
            const QA_COL_NUM = 14;
            const QA_COL_POINT = 10;
            const QA_COL_ANSWER = 10;
            const QA_COL_MY_ANS = 14;
            const QA_COL_CHECK = 10;
            const QA_COL_RATE = 14;
            const QA_COL_RANK = 12;
            const QA_FIXED_W = QA_COL_NUM + QA_COL_POINT + QA_COL_ANSWER + QA_COL_MY_ANS + QA_COL_CHECK + QA_COL_RATE + QA_COL_RANK;
            const QA_COL_CHOICE = (PAGE_WIDTH - PAGE_MARGIN * 2 - QA_FIXED_W) / 5;
            const QA_X = PAGE_MARGIN;
            const QA_X_POINT = QA_X + QA_COL_NUM;
            const QA_X_ANSWER = QA_X_POINT + QA_COL_POINT;
            const QA_X_MY_ANS = QA_X_ANSWER + QA_COL_ANSWER;
            const QA_X_CHECK = QA_X_MY_ANS + QA_COL_MY_ANS;
            const QA_X_RATE = QA_X_CHECK + QA_COL_CHECK;
            const QA_X_RANK = QA_X_RATE + QA_COL_RATE;
            const QA_X_CHOICE = QA_X_RANK + QA_COL_RANK;
            const drawQaTableHeader = (qaY) => {
                pdf.setFillColor(...DIVIDER_FILL);
                pdf.rect(QA_X, qaY, QA_COL_NUM, QA_HDR_H * 2, 'F');
                pdf.setDrawColor(208, 211, 217);
                pdf.rect(QA_X, qaY, QA_COL_NUM, QA_HDR_H * 2);
                pdf.setFont(FONT_FAMILY, 'bold');
                pdf.setFontSize(8);
                pdf.setTextColor(16, 16, 16);
                const qnMidX = QA_X + QA_COL_NUM / 2;
                pdf.text('문항', qnMidX, qaY + 5, { align: 'center' });
                pdf.text('번호', qnMidX, qaY + 9.5, { align: 'center' });
                drawCell(pdf, QA_X_POINT, qaY, QA_COL_POINT, QA_HDR_H * 2, '배점', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, QA_X_ANSWER, qaY, QA_COL_ANSWER, QA_HDR_H * 2, '정답', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, QA_X_MY_ANS, qaY, QA_COL_MY_ANS, QA_HDR_H * 2, '내 답안', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, QA_X_CHECK, qaY, QA_COL_CHECK, QA_HDR_H * 2, '채점', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, QA_X_RATE, qaY, QA_COL_RATE + QA_COL_RANK, QA_HDR_H, '오답률(%)', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, QA_X_CHOICE, qaY, QA_COL_CHOICE * 5, QA_HDR_H, '답지반응률(%)', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                const qaY2 = qaY + QA_HDR_H;
                drawCell(pdf, QA_X_RATE, qaY2, QA_COL_RATE, QA_HDR_H, '%', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                drawCell(pdf, QA_X_RANK, qaY2, QA_COL_RANK, QA_HDR_H, '순위', { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                ['①', '②', '③', '④', '⑤'].forEach((label, ci) => {
                    drawCell(pdf, QA_X_CHOICE + QA_COL_CHOICE * ci, qaY2, QA_COL_CHOICE, QA_HDR_H, label, { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                });
            };
            const drawQaRow = (q, rowY) => {
                const myAnsColor = q.correction === 'X' ? RED_TEXT : [16, 16, 16];
                drawCell(pdf, QA_X, rowY, QA_COL_NUM, QA_ROW_H, String(q.number), { fontSize: 8 });
                drawCell(pdf, QA_X_POINT, rowY, QA_COL_POINT, QA_ROW_H, String(q.point), { fontSize: 8 });
                drawCell(pdf, QA_X_ANSWER, rowY, QA_COL_ANSWER, QA_ROW_H, q.answer || '-', { fontSize: 8 });
                drawCell(pdf, QA_X_MY_ANS, rowY, QA_COL_MY_ANS, QA_ROW_H, q.my_answer || '-', { fontSize: 8, textColor: myAnsColor });
                drawCell(pdf, QA_X_CHECK, rowY, QA_COL_CHECK, QA_ROW_H, q.correction || '-', { fontSize: 8 });
                drawCell(pdf, QA_X_RATE, rowY, QA_COL_RATE, QA_ROW_H, String(q.rating ?? '-'), { fontSize: 8 });
                drawCell(pdf, QA_X_RANK, rowY, QA_COL_RANK, QA_ROW_H, q.rank ? String(q.rank) : '', { fontSize: 8 });
                const isShortAnswer = q.choice1 === 0 && q.choice2 === 0 && q.choice3 === 0 && q.choice4 === 0 && q.choice5 === 0;
                // choice0~choice5가 모두 0(또는 없음)이면 보기 반응률이 없는 문항(주관식 등)이므로 빈칸 처리
                const choiceTotal = [q.choice0, q.choice1, q.choice2, q.choice3, q.choice4, q.choice5]
                    .reduce((sum, v) => sum + (Number(v) || 0), 0);
                const isAllZeroChoice = choiceTotal === 0;
                if (isAllZeroChoice) {
                    drawCell(pdf, QA_X_CHOICE, rowY, QA_COL_CHOICE * 5, QA_ROW_H, '', { fontSize: 8 });
                }
                else if (isShortAnswer) {
                    drawCell(pdf, QA_X_CHOICE, rowY, QA_COL_CHOICE * 5, QA_ROW_H, q.choice0 != null ? String(q.choice0) : '', { fontSize: 8 });
                }
                else {
                    [q.choice1, q.choice2, q.choice3, q.choice4, q.choice5].forEach((val, ci) => {
                        drawCell(pdf, QA_X_CHOICE + QA_COL_CHOICE * ci, rowY, QA_COL_CHOICE, QA_ROW_H, val != null ? String(val) : '', { fontSize: 8 });
                    });
                }
            };
            const drawQaPageTop = (startY) => {
                qaCurrentY = startY ?? PAGE_MARGIN;
                pdf.setFont(FONT_FAMILY, 'bold');
                pdf.setFontSize(11);
                pdf.setTextColor(16, 16, 16);
                pdf.text('문항별 분석', PAGE_MARGIN, qaCurrentY + 8);
                qaCurrentY += 12;
                drawQaTableHeader(qaCurrentY);
                qaCurrentY += QA_HDR_H * 2;
            };
            const ensureSpace = (neededH) => {
                if (qaCurrentY + neededH > PAGE_HEIGHT - BOTTOM_MARGIN) {
                    pdf.addPage();
                    drawQaPageTop();
                }
            };
            if (shouldShowGradeReport) {
                pdf.addPage();
                drawQaPageTop();
            }
            else {
                drawQaPageTop(chartSectionStartY);
            }
            const drawQaGroup = (label, questions) => {
                if (questions.length === 0)
                    return;
                ensureSpace(QA_ROW_H);
                drawCell(pdf, QA_X, qaCurrentY, TOTAL_W, QA_ROW_H, label, { fillColor: DIVIDER_FILL, fontStyle: 'bold', fontSize: 8 });
                qaCurrentY += QA_ROW_H;
                for (const q of questions) {
                    ensureSpace(QA_ROW_H);
                    drawQaRow(q, qaCurrentY);
                    qaCurrentY += QA_ROW_H;
                }
            };
            if (commonQs.length === 0) {
                for (const q of selectiveQs) {
                    ensureSpace(QA_ROW_H);
                    drawQaRow(q, qaCurrentY);
                    qaCurrentY += QA_ROW_H;
                }
            }
            else {
                drawQaGroup('공통', commonQs);
                if (selectiveQs.length > 0) {
                    drawQaGroup('선택', selectiveQs);
                }
            }
        }
    }
    return pdf;
};
exports.buildScoreReportPdf = buildScoreReportPdf;
const openScoreReportPdf = async (payload) => {
    const pdf = await (0, exports.buildScoreReportPdf)(payload);
    return new Uint8Array(pdf.output('arraybuffer'));
};
exports.openScoreReportPdf = openScoreReportPdf;
const downloadScoreReportPdf = async (payload, filename) => {
    const pdf = await (0, exports.buildScoreReportPdf)(payload);
    const testName = payload.report.test_name || '';
    const memberName = payload.report.member_name || '';
    const resolvedFilename = filename || `${[testName, memberName].filter(Boolean).join(' - ')} 성적표.pdf`;
    const blob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = resolvedFilename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
};
exports.downloadScoreReportPdf = downloadScoreReportPdf;
